import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import moment, { Moment } from '@e3dc-react/shell/Libs/moment'
import usePortalQuery from 'src/Libs/api/hooks/use-portal-query'
import { useEffect, useState } from 'react'
import { fetchPowerPrices } from '../api/power-price.api'
import { NullablePowerPrice, PowerPrice } from '../types'
import { getCurrentPowerPrice } from '../utils/power-price-widget'

type UsePowerPricesHookProps = {
  installationId: string | undefined
  fromDate?: Moment
  toDate?: Moment
  refetchInterval?: number
  enabled?: boolean
}

interface UsePowerPricesHook {
  powerPrices: PowerPrice[] | undefined
  currentPowerPriceValues: PowerPrice
  lastFetchedTimestamp: string
  isPowerPricesLoading: boolean
}

const defaultCurrentPowerPriceValues = { price: 0, validFrom: '0', validTo: '0' }

/**
 * Custom React hook for fetching power prices for a given installation over a specified time range.
 * It leverages a polling mechanism to periodically refetch the data based on a provided interval.
 *
 * The hook integrates with an authentication-enabled fetch function and custom translation utilities
 * to handle data fetching and error management gracefully. It returns the current power prices,
 * the last fetched timestamp, and loading state information.
 *
 * @param {Object} params - The parameters for fetching power prices.
 * @param {string} params.installationId - The ID of the installation for which to fetch power prices.
 * @param {Moment} [params.fromDate] - An optional getter function for the start time for the price data fetch.
 * @param {Moment} [params.toDate] - An optional getter function for the end time for the price data fetch.
 * @param {number} [params.refetchInterval] - An optional interval in milliseconds to periodically refetch the data.
 * @returns {UsePowerPricesHook} An object containing the power prices data, last fetched timestamp, and loading state.
 */
const usePowerPrices = ({ installationId, fromDate, toDate, refetchInterval, enabled }: UsePowerPricesHookProps): UsePowerPricesHook => {
  const authFetch = useAuthFetchWithResponse()
  const { t } = useCustomTranslation()
  const [from, setFrom] = useState(fromDate)
  const [to, setTo] = useState(toDate)
  const [lastFetchedTimestamp, setLastFetchedTimestamp] = useState<string>(moment().format(t('common.format.dateTime')))
  const [currentPowerPriceValues, setCurrentPowerPriceValues] = useState<PowerPrice>(defaultCurrentPowerPriceValues)

  const {
    data: powerPrices,
    isLoading,
    isSuccess,
    isError,
  } = usePortalQuery<NullablePowerPrice[], PowerPrice[]>({
    queryKey: ['power-prices', installationId, from?.toISOString(), to?.toISOString()],
    queryFn: () => fetchPowerPrices(installationId, authFetch, from, to),
    enabled: !!enabled,
    select: (response: NullablePowerPrice[] | null): PowerPrice[] => {
      const pricesWithoutNullValues: PowerPrice[] =
        response?.map(value => ({
          price: value.price ?? 0,
          validFrom: value.validFrom,
          validTo: value.validTo,
        })) ?? []

      if (response) return pricesWithoutNullValues

      return []
    },
  })

  useEffect(() => {
    if (!isLoading && isSuccess) {
      setLastFetchedTimestamp(moment().format(t('common.format.dateTime')))

      const currentPowerPrice = getCurrentPowerPrice(powerPrices ?? [])
      setCurrentPowerPriceValues(currentPowerPrice || defaultCurrentPowerPriceValues)
    }
  }, [isLoading, isSuccess, powerPrices, t])

  useEffect(() => {
    if (refetchInterval) {
      /* update "from" and "to" values every x minutes to refetch data */
      const interval = setInterval(() => {
        setFrom(fromDate)
        setTo(toDate)
      }, refetchInterval)

      return () => {
        clearInterval(interval)
      }
    }
  }, [fromDate, toDate, refetchInterval])

  return {
    powerPrices: isError ? [] : powerPrices,
    currentPowerPriceValues,
    lastFetchedTimestamp,
    isPowerPricesLoading: isLoading,
  }
}

export default usePowerPrices
